import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiBox,
  MiButton,
  MiControlledSelectBox,
  MiModalConfirm,
  MiPlaceholderList,
  MiSectionHeader,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ICONS_WEB,
  MODAL_TITLES,
} from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import * as mdi from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { Box, useTheme } from "@mui/material";
import { useAccountAdderBucketsTabViewModel } from "./useAccountAdderBucketsTabViewModel";

const schema = z.object({
  adders: z
    .array(
      z.object({
        id: z.string().uuid(),
      })
    )
    .optional(),
});

export function AccountAdderBucketsTab({
  stateManager,
  accountDetail,
  editMode,
  setEditMode,
}) {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const {
    control,
    formState: { isValid, isDirty },
    reset,
    getValues,
    setValue,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      adders: [],
    },
    resolver: zodResolver(schema),
  });
  const { fields, append, remove } = useFieldArray({ control, name: "adders" });
  const {
    isLoading,
    adderBuckets,
    adderOptions,
    getAccountAdderBuckets,
    getAdderOptions,
    handleSaveAdderBuckets,
    isRemoved,
    handleAddAdder,
    handleRemoveAdder,
  } = useAccountAdderBucketsTabViewModel({
    stateManager,
    setEditMode,
    getValues,
    setValue,
    append,
    remove,
    reset,
  });
  const { adders } = getValues();
  const { classes } = useStyles();
  const theme = useTheme();

  useEffect(() => {
    if (accountDetail?.id) {
      getAdderOptions();
      getAccountAdderBuckets(accountDetail.id);
    }
  }, [accountDetail?.id]);

  return (
    <>
      <MiBox>
        <Box className={classes.leftContainer}>
          <MiSectionHeader
            title="Assign Adder Buckets"
            subtitle={`Link Adder Buckets that apply to ${accountDetail?.name}.`}
            leftIcon={mdi[ICONS_WEB.ARCHIVE_OUTLINE]}
            rightIcon={!editMode && mdi[ICONS_WEB.PENCIL_OUTLINE]}
            onRightIconClick={() => setEditMode(true)}
          />

          {!editMode && adders.length === 0 && !isLoading && (
            <MiPlaceholderList
              icon={mdi[ICONS_WEB.BULLET_LIST]}
              text="No adder buckets set up for this customer."
              helperText="To create, begin editing and click + Add Adder Bucket button."
            />
          )}
          <Box>
            {fields.map((f, i) => (
              <Box key={f.id} className={classes.adderBox}>
                <MiControlledSelectBox
                  formControl={control}
                  name={`adders[${i}].id`}
                  label={`Adder #${i + 1}`}
                  options={adderOptions}
                  required={editMode}
                  readOnly={!editMode}
                  disableNone
                  disabled={isLoading || !editMode || isRemoved(i)}
                  sx={{ background: isRemoved(i) && theme.palette.red.light }}
                />
                {editMode && (
                  <MiButton
                    className={classes.addDelBtn}
                    onClick={() => handleRemoveAdder(i)}
                    startIcon={{
                      path: isRemoved(i)
                        ? mdi[ICONS_WEB.ARROW_U_LEFT_TOP]
                        : mdi[ICONS_WEB.MINUS],
                    }}
                    color={
                      isRemoved(i) ? BUTTON_COLORS.PRIMARY : BUTTON_COLORS.RED
                    }
                    variant={BUTTON_VARIANTS.CONTAINED}
                    disabled={isLoading}
                  />
                )}
              </Box>
            ))}
            {editMode && (
              <MiButton
                title="Add Adder Bucket"
                onClick={handleAddAdder}
                variant={BUTTON_VARIANTS.OUTLINED}
                startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
                disabled={isLoading}
              />
            )}
          </Box>
        </Box>
      </MiBox>
      <Footer
        show={editMode}
        saveTitle={MODAL_TITLES.SAVE}
        isDirty={isDirty}
        handleCancelClick={() => {
          setEditMode(false);
          reset();
        }}
        handleSaveClick={() => {
          if (adderBuckets.length > 0) {
            setOpenConfirmModal(true);
            return;
          }
          handleSaveAdderBuckets(adders, accountDetail?.id);
        }}
        disableSave={!isValid || isLoading || !isDirty}
      />
      <MiModalConfirm
        title={MODAL_TITLES.SAVE}
        open={openConfirmModal}
        handleNegative={() => setOpenConfirmModal(false)}
        handlePositive={() => {
          handleSaveAdderBuckets(adders, accountDetail?.id);
          setOpenConfirmModal(false);
        }}
        description={`Are you sure you want to modify the Adder Buckets Setup for ${accountDetail?.name}?`}
        buttonPositiveColor={BUTTON_COLORS.PRIMARY}
      />
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  leftContainer: {
    width: "50%",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  adderBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: "70%",
  },
  addDelBtn: {
    borderRadius: "50%",
    padding: theme.spacing(0),
  },
}));
